/**
 * The app allow-list logic of one workspace, apart from the chips that draw it
 * (`WorkspaceApps.tsx`), so it can be tested on its own.
 *
 * Restricted apps (BARN-345, contract C1): a workspace `apps: null` ("all apps")
 * means every app of the served version EXCEPT the restricted ones, so a
 * restricted app is on only when the list names it, and a list collapses back
 * to `null` only when every non-restricted app is on and no restricted one is.
 * With no restricted app every function here gives exactly the old inline
 * results.
 */
import { expandAppList } from '../../../../../lib/restricted-apps'

/**
 * The keys that get a chip: the version's apps (without `worker`, which is the
 * API and never a choice) plus its restricted apps, which the harness may have
 * injected into appsConfig without listing them in `meta.apps`.
 */
export function chipKeys(allApps: string[], restrictedKeys: string[]): string[] {
  const keys = allApps.filter(a => a !== 'worker')
  for (const k of restrictedKeys) if (k !== 'worker' && !keys.includes(k)) keys.push(k)
  return keys
}

/** Keys on record that the served version does not offer (kept, never counted). */
export function dormantKeys(apps: string[] | null, chips: string[]): string[] {
  return (apps ?? []).filter(a => a !== 'worker' && !chips.includes(a))
}

export function isOn(apps: string[] | null, key: string, restrictedKeys: string[]): boolean {
  if (apps === null) return !restrictedKeys.includes(key)
  return apps.includes(key)
}

/**
 * Toggle one app. From `null` the start is every non-restricted chip. The result
 * collapses to `null` only when every non-restricted chip is on and no
 * restricted app is: counting `next` wholesale would let dormant keys cross the
 * threshold, and `null` would silently drop a restricted app.
 */
export function toggle(
  apps: string[] | null,
  key: string,
  chips: string[],
  restrictedKeys: string[],
): string[] | null {
  const restricted = new Set(restrictedKeys)
  const open = chips.filter(a => !restricted.has(a))
  const current = expandAppList(apps, open, {})
  const next = current.includes(key) ? current.filter(x => x !== key) : [...current, key]
  const openSelected = next.filter(x => open.includes(x)).length
  const anyRestricted = next.some(x => restricted.has(x))
  return openSelected >= open.length && !anyRestricted ? null : next
}

/** The "all" link: back to `null`, which never includes a restricted app. */
export function resetToAll(): null {
  return null
}
